/**
 * Único ponto de contato entre a observabilidade do React e o grafo Angular.
 *
 * O environment e o cliente do SDK entram na pasta por aqui; nenhum outro arquivo de
 * produção de `src/react/observability/` importa código de fora de `src/react/`.
 * Concentrar a travessia num arquivo é o que mantém o restante da camada — fingerprint,
 * deduplicação, sanitização — verificável sem browser e sem SDK real.
 */
import { environment } from 'environments/environment';
import {
  iniciarClientePostHog,
  obterClientePostHog,
  resetClientePostHogParaTestes,
} from 'app/services/posthog/posthog-client';

import { ExceptionProperties } from './types';

/** Delega ao carregamento memoizado do SDK. Nunca rejeita. */
export function startObservabilityClient(): Promise<void> {
  // Repasse direto de propósito: a promessa é memoizada no módulo do cliente, então uma
  // montagem de folha Angular que inicialize o SDK depois compartilha este mesmo
  // carregamento em vez de disparar um segundo posthog.init. O cliente também trata a
  // própria falha de carga internamente, e é daí que vem a garantia de não rejeitar.
  return iniciarClientePostHog();
}

/** Gate de ambiente. Única leitura de environment.posthogEnabled no lado React. */
export function isObservabilityEnabled(): boolean {
  return environment.posthogEnabled;
}

/** Cliente do SDK disponível para emissão. */
export function isClientReady(): boolean {
  return obterClientePostHog() !== null;
}

/**
 * Emite a exceção pelo evento reservado do SDK. Nunca lança.
 *
 * O valor original vai no primeiro argumento porque é dele que o SDK extrai o stack
 * nativo; as propriedades de metadado entram no segundo, no mesmo evento. A falha de uma
 * emissão é engolida aqui: quem chama está no caminho de erro e não tem como tratar um
 * segundo.
 */
export function emitException(error: unknown, properties: ExceptionProperties): void {
  try {
    obterClientePostHog()?.captureException(error, properties);
  } catch {
    // Emissão é melhor-esforço: sem cliente, sem rede ou com o SDK em estado inválido, o
    // evento se perde e a aplicação segue.
  }
}

/** Descarta o estado de módulo entre testes. Não usar em código de produção. */
export function resetObservabilityBridgeForTests(): void {
  resetClientePostHogParaTestes();
}
